import { errorText } from './errors.mjs';

/** Release a host response without pulling its body (redirects, abort races and bodyless statuses). */
async function closePublicHttpResponse(upstream, reason, iterator = upstream.body[Symbol.asyncIterator]()) {
  try { upstream.cancel(reason instanceof Error ? reason : undefined); }
  finally { await iterator.return?.(); }
}

/** Adapt host bytes without read-ahead. The host still owns the socket and upload lifetime. */
async function publicHttpResponse(upstream, signal, redirected, releaseSignal) {
  const iterator = upstream.body[Symbol.asyncIterator]();
  let finished = false;
  let onAbort;
  const detach = () => {
    signal?.removeEventListener('abort', onAbort);
    releaseSignal();
  };
  const stop = async (reason) => {
    if (finished) return;
    finished = true;
    detach();
    await closePublicHttpResponse(upstream, reason, iterator);
  };

  const bodyless = [204, 205, 304].includes(upstream.status);
  let body = null;
  if (bodyless) {
    await stop();
    signal?.throwIfAborted();
  } else {
    body = new ReadableStream({
      start(controller) {
        onAbort = () => {
          controller.error(signal.reason);
          // Event listeners cannot return cleanup promises; route failures to the response stream.
          void stop(signal.reason).catch(error => controller.error(error));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
        if (signal?.aborted) onAbort();
      },
      async pull(controller) {
        try {
          const chunk = await iterator.next();
          if (finished) return;
          if (chunk.done) {
            finished = true;
            detach();
            controller.close();
          } else controller.enqueue(chunk.value);
        } catch (error) {
          controller.error(error);
          await stop(error);
        }
      },
      cancel: stop,
    }, { highWaterMark: 0 });
  }

  const response = new Response(body, {
    status: upstream.status, statusText: upstream.statusText, headers: upstream.headers,
  });
  Object.defineProperties(response, {
    url: { value: upstream.url },
    redirected: { value: redirected },
  });
  return response;
}

/** Unlike a stream's queued async-iterator return(), reader.cancel() wakes a pending upload read. */
function publicHttpUpload(stream) {
  const reader = stream.getReader();
  // Observe terminal producer state without pulling; cancel() on an errored stream repeats that
  // producer error, whereas a failing cancellation callback is a distinct cleanup failure.
  const closed = reader.closed.then(() => ({ failed: false }), error => ({ failed: true, error }));
  let finished = false;
  let stopping;
  const close = () => {
    if (stopping) return stopping;
    if (finished) return Promise.resolve();
    finished = true;
    stopping = (async () => {
      try { await reader.cancel(); }
      catch (error) {
        const terminal = await closed;
        // The host already owns the producer outcome. Do not replace its refusal/status with a
        // repeated terminal producer error when disposing an upload it never read.
        if (!terminal.failed || terminal.error !== error) throw error;
      } finally { reader.releaseLock(); }
    })();
    return stopping;
  };
  return {
    close,
    body: {
      [Symbol.asyncIterator]() {
        return {
          async next() {
            if (finished) return { done: true, value: undefined };
            try {
              const chunk = await reader.read();
              if (chunk.done && !finished) {
                finished = true;
                reader.releaseLock();
              }
              return chunk;
            } catch (error) {
              if (!finished) {
                finished = true;
                reader.releaseLock();
              }
              throw error;
            }
          },
          async return() {
            await close();
            return { done: true, value: undefined };
          },
        };
      },
    },
  };
}

/**
 * Native body/Response codec over the lazy host public HTTP request seam, never a network fallback.
 * Writes are single-attempt/manual; only explicit GET follow permits up to five validated redirects.
 */
export function createPublicHttpFetch(getTransport) {
  return async (input, init = {}) => {
    // SDKs such as grammY supply an event-compatible signal without native reason/throwIfAborted.
    // Normalize once here; the host continues receiving only its native AbortSignal contract.
    const source = init.signal;
    const cancellation = source ? new AbortController() : undefined;
    const signal = cancellation?.signal;
    let listening = false;
    const releaseSignal = () => {
      if (!listening) return;
      listening = false;
      source.removeEventListener('abort', forwardAbort);
    };
    const forwardAbort = () => {
      releaseSignal();
      cancellation.abort(source.reason);
    };
    if (source) {
      if (source.aborted) forwardAbort();
      else {
        source.addEventListener('abort', forwardAbort, { once: true });
        listening = true;
      }
    }
    try {
      signal?.throwIfAborted();
      const method = (init.method ?? 'GET').toUpperCase();
      if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) throw new TypeError('Unsupported public HTTP method');
      if (method === 'GET' && init.body != null) throw new TypeError('GET cannot have a body');
      const redirect = init.redirect ?? 'manual';
      if (!['manual', 'error', 'follow'].includes(redirect)) throw new TypeError('Invalid public HTTP redirect mode');
      if (redirect === 'follow' && method !== 'GET') throw new TypeError('Only GET can follow redirects');

      const transport = getTransport();
      let target = new URL(input);
      target.hash = '';
      let headers = new Headers(init.headers);
      let body = init.body;
      let upload;
      // Keep fixed-size bytes visible to the host's framing owner (not chunked). SDK byte iterables
      // already carry their encoding; only native multipart/blob/readable bodies need a stream bridge.
      if (ArrayBuffer.isView(body)) body = new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
      else if (body instanceof ArrayBuffer) body = new Uint8Array(body);
      else if (body != null && (body instanceof ReadableStream || !body[Symbol.asyncIterator])) {
        const encoded = new Response(body, { headers });
        headers = encoded.headers;
        if (typeof body === 'string' || body instanceof URLSearchParams) body = String(body);
        else {
          upload = publicHttpUpload(encoded.body);
          body = upload.body;
        }
      }

      const visited = new Set([target.href]);
      for (let hops = 0; ; hops++) {
        signal?.throwIfAborted();
        let upstream;
        try {
          upstream = await transport.request(target.href, {
            method, headers: Object.fromEntries(headers), body: body ?? undefined, signal: signal ?? undefined,
          });
        } finally {
          // A host refusal may precede iterator acquisition; an early response may interrupt next().
          try { await upload?.close(); }
          catch (error) {
            if (upstream) await closePublicHttpResponse(upstream, error);
            throw error;
          }
        }
        if (signal?.aborted) {
          await closePublicHttpResponse(upstream, signal.reason);
          signal.throwIfAborted();
        }
        if (![301, 302, 303, 307, 308].includes(upstream.status) || redirect === 'manual') {
          return await publicHttpResponse(upstream, signal, hops > 0, releaseSignal);
        }

        await closePublicHttpResponse(upstream);
        signal?.throwIfAborted();
        if (redirect === 'error') throw new TypeError('Public HTTP redirect refused');
        if (hops === 5) throw new TypeError('Public HTTP redirect limit exceeded');
        const location = new Headers(upstream.headers).get('location');
        if (!location) throw new TypeError('Missing public HTTP redirect location');
        let next;
        try { next = new URL(location, target); }
        catch { throw new TypeError('Invalid public HTTP redirect location'); }
        next.hash = '';
        if (target.protocol === 'https:' && next.protocol === 'http:') throw new TypeError('Public HTTP redirect downgrade refused');
        if (visited.has(next.href)) throw new TypeError('Public HTTP redirect loop');
        if (next.origin !== target.origin) {
          headers.delete('authorization');
          headers.delete('cookie');
          headers.delete('proxy-authorization');
        }
        visited.add(next.href);
        target = next;
      }
    } catch (error) {
      releaseSignal();
      throw error;
    }
  };
}

/**
 * One HTTP client for plugins that talk to an external API — so every integration gets the same
 * timeout, the same concurrency ceiling and the same shape of failure, instead of
 * each one inventing its own `fetch` wrapper and getting a different subset right.
 *
 * Headers carry credentials, so the client never logs them. The integration owns retry decisions.
 */


/** A failed request as DATA: the status when the server answered, the parsed/raw body it answered with,
 *  and a message safe to show a user. `status` is null when the request never got an answer at all
 *  (transport error, timeout, abort) — a distinction the caller needs, because only one of the two says
 *  anything about what the far end thinks. */
export class HttpError extends Error {
  constructor(message, { status = null, data = undefined, body = '', url = '', method = '', cause = undefined, timedOut = false, aborted = false } = {}) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
    this.body = body;
    this.url = url;
    this.method = method;
    this.timedOut = timedOut;
    this.aborted = aborted;
  }
}

/** Decode a response body. A broken JSON payload from a misbehaving server must surface as an error the
 *  caller can read, not as an exception from inside the parser that hides both the status and the text, so
 *  parse failures yield `data: undefined` with the raw text. A failed body read still rejects: the caller
 *  classifies it, because an empty body after a timeout is not an answer. Returns the decoded value plus the
 *  raw text it came from. */
async function readBody(res, responseType) {
  if (responseType === 'bytes') return { data: undefined, text: '', bytes: new Uint8Array(await res.arrayBuffer()) };
  const text = await res.text();
  if (text === '') return { data: undefined, text };
  const type = res.headers.get('content-type') ?? '';
  if (!/\bjson\b/i.test(type)) return { data: text, text };
  try { return { data: JSON.parse(text), text }; }
  catch { return { data: undefined, text }; }
}

/** Retry-After delay-seconds or HTTP date; missing and invalid values leave policy to the caller. */
export function retryAfterMs(value, nowMs = Date.now()) {
  if (value == null || value.trim() === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    const delay = seconds * 1000;
    return seconds >= 0 && Number.isFinite(delay) ? delay : null;
  }
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - nowMs) : null;
}


/** Serialize a query object. `undefined`/`null` values are dropped (an absent filter is not the string
 *  "undefined"); an array becomes one repeated key, which is what nearly every API expects. */
function queryString(query) {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) for (const v of value) { if (v !== undefined && v !== null) params.append(key, String(v)); }
    else params.append(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** True for a value that has to be serialized before it can be sent — i.e. a plain object or array, and
 *  nothing the platform already knows how to put on the wire. */
function isPlainBody(body) {
  if (body === undefined || body === null) return false;
  if (typeof body === 'string') return false;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
  if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) return false;
  if (ArrayBuffer.isView(body) || body instanceof ArrayBuffer) return false;
  return typeof body === 'object';
}

function joinUrl(baseUrl, path) {
  if (!baseUrl) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${baseUrl.replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;
}

/** A ceiling on requests in flight. An integration that fans out over a list must not open a hundred
 *  sockets at once: the far end rate-limits it, and the daemon shares its event loop with every other
 *  plugin. Queued callers start in the order they arrived. */
function createLimiter(max) {
  let active = 0;
  const waiting = [];
  const next = () => {
    if (active >= max) return;
    const run = waiting.shift();
    if (!run) return;
    active += 1;
    run();
  };
  return async function limit(fn) {
    if (active >= max) await new Promise((resolve) => { waiting.push(resolve); });
    else active += 1;
    try { return await fn(); }
    finally { active -= 1; next(); }
  };
}

/**
 * Build a client bound to one API.
 *
 *  - `baseUrl` / `headers` — prefix and the headers every call carries (auth, accept). Per-call headers
 *    merge over these.
 *  - `timeoutMs` — the request budget begins inside its concurrency slot.
 *  - `maxConcurrent` — requests in flight from this client.
 *  - `fetchImpl` — injectable for tests.
 */
export function createHttpClient({
  baseUrl = '',
  headers: baseHeaders = {},
  timeoutMs = 15000,
  maxConcurrent = 4,
  fetchImpl = globalThis.fetch,
} = {}) {
  const limit = createLimiter(Math.max(1, maxConcurrent));

  async function request(path, options = {}) {
    const method = (options.method ?? 'GET').toUpperCase();
    const url = joinUrl(baseUrl, path) + queryString(options.query);
    const requestTimeout = options.timeoutMs ?? timeoutMs;
    const outer = options.signal;

    const headers = { ...baseHeaders, ...(options.headers ?? {}) };
    // A plain object is the common case and becomes JSON; anything the platform can already send as a
    // body (multipart form, blob, stream, bytes) is passed through untouched — serializing a FormData
    // would turn a file upload into the string "[object FormData]".
    let body = options.body;
    if (isPlainBody(body)) {
      body = JSON.stringify(body);
      if (!Object.keys(headers).some((h) => h.toLowerCase() === 'content-type')) headers['content-type'] = 'application/json';
    }

    // Queue wait is not server time: start the deadline only when the slot opens.
    let timeout = null;
    let res;
    try {
      res = await limit(() => {
        timeout = AbortSignal.timeout(requestTimeout);
        return fetchImpl(url, {
          method, headers, body, signal: outer ? AbortSignal.any([outer, timeout]) : timeout,
          ...(options.redirect === undefined ? {} : { redirect: options.redirect }),
        });
      });
    } catch (error) {
      if (outer?.aborted) throw new HttpError('request aborted', { url, method, cause: error, aborted: true });
      const timedOut = timeout?.aborted === true;
      throw new HttpError(timedOut ? `request timed out after ${requestTimeout} ms` : `request failed: ${errorText(error)}`, {
        url, method, cause: error, timedOut,
      });
    }
    let data;
    let text;
    let bytes;
    try {
      ({ data, text, bytes } = await readBody(res, options.responseType));
    } catch (error) {
      if (outer?.aborted) throw new HttpError('request aborted', { status: res.status, url, method, cause: error, aborted: true });
      const timedOut = timeout?.aborted === true;
      throw new HttpError(timedOut ? `request timed out after ${requestTimeout} ms` : `response body could not be read: ${errorText(error)}`, {
        status: res.status, url, method, cause: error, timedOut,
      });
    }
    if (!res.ok && options.throwOnHttpError !== false) throw new HttpError(`${method} failed with ${res.status}`, { status: res.status, data, body: text, url, method });
    return { ok: res.ok, status: res.status, headers: res.headers, data, text, ...(bytes === undefined ? {} : { bytes }) };
  }

  return { request };
}
